import React, { useState } from 'react';
import { Box, Checkbox, InputAdornment, TextField, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import CommonModalButton from '../../../form-components/CommonModalButton';

/**
 * 지점 다중 선택 모달 (#73 판매·이용지점). 검색 + 전체 선택 + 개별 체크박스.
 * 지점명이 전부 "센트리얼 ○○ [○○점]" 형태로 접두어가 같아 초성/알파벳 그룹핑은
 * 실효가 없어 적용하지 않는다 — 이름순 정렬 + 검색만으로 20개 안팎을 감당한다.
 */
const BranchSelectModal = ({ open, title, branches = [], selectedIds = [], onChangeIds, onClose }) => {
  const [search, setSearch] = useState('');

  const sortedBranches = [...branches].sort((a, b) => a.branchName.localeCompare(b.branchName, 'ko'));
  const filteredBranches = search
    ? sortedBranches.filter(b => b.branchName.toLowerCase().includes(search.toLowerCase()))
    : sortedBranches;

  const selectedCount = selectedIds.length;
  const totalCount = branches.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const toggleBranch = branchId => {
    if (selectedIds.includes(branchId)) {
      if (selectedIds.length <= 1) return; // 최소 1개 지점 유지
      onChangeIds(selectedIds.filter(id => id !== branchId));
    } else {
      onChangeIds([...selectedIds, branchId]);
    }
  };

  const toggleAll = () => {
    const targetIds = filteredBranches.map(b => b.branchId);
    const allFilteredSelected = targetIds.length > 0 && targetIds.every(id => selectedIds.includes(id));
    if (allFilteredSelected) {
      const next = selectedIds.filter(id => !targetIds.includes(id));
      onChangeIds(next.length > 0 ? next : [targetIds[0]]); // 최소 1개 지점 유지
    } else {
      onChangeIds([...new Set([...selectedIds, ...targetIds])]);
    }
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '440px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
      }}>
        <Box sx={{
          backgroundColor: '#555', height: '52px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderRadius: '10px 10px 0 0', padding: '0 20px',
        }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>{title}</Typography>
          <CloseIcon onClick={handleClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </Box>

        {/* 검색 */}
        <Box sx={{ p: '12px 16px', backgroundColor: '#fafafa', borderBottom: '1px solid #eee' }}>
          <TextField
            size="small" fullWidth
            placeholder="지점명 검색..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#4AB3BC' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '36px', backgroundColor: '#fff', borderRadius: '6px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
              },
            }}
          />
        </Box>

        {/* 전체 선택 */}
        <Box
          onClick={toggleAll}
          sx={{
            display: 'flex', alignItems: 'center', px: '16px', py: '10px',
            cursor: 'pointer', borderBottom: '1px solid #eee',
            backgroundColor: allSelected ? '#e8f5f5' : '#fff',
            '&:hover': { backgroundColor: allSelected ? '#ddf0f0' : '#f5f5f5' },
          }}>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            size="small"
            sx={{ p: 0, mr: 1.5, color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }}
          />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>전체 선택</Typography>
          <Typography sx={{ fontSize: '13px', color: '#4AB3BC', fontWeight: 500, ml: 'auto' }}>
            {selectedCount}/{totalCount}
          </Typography>
        </Box>

        {/* 지점 목록 */}
        <Box sx={{
          maxHeight: '360px', overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
        }}>
          {filteredBranches.length === 0 ? (
            <Typography sx={{ p: '16px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
              검색 결과가 없습니다
            </Typography>
          ) : (
            filteredBranches.map(branch => {
              const isChecked = selectedIds.includes(branch.branchId);
              return (
                <Box
                  key={branch.branchId}
                  onClick={() => toggleBranch(branch.branchId)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: '16px', py: '8px',
                    cursor: 'pointer',
                    backgroundColor: isChecked ? '#f0f8f8' : 'transparent',
                    borderLeft: isChecked ? '3px solid #4AB3BC' : '3px solid transparent',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'all 0.1s ease',
                    '&:hover': { backgroundColor: isChecked ? '#e8f5f5' : '#f8f8f8' },
                  }}>
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    sx={{ p: 0, mr: 1.5, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' } }}
                  />
                  <Typography sx={{ fontSize: '14px', color: isChecked ? '#333' : '#555' }}>
                    {branch.branchName}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>

        {/* 푸터 */}
        <Box sx={{
          display: 'flex', justifyContent: 'center', gap: '10px',
          padding: '14px 20px', borderTop: '1px solid #eee', backgroundColor: '#fafafa',
        }}>
          <CommonModalButton variant="cancel" onClick={handleClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleClose}>
            선택 완료 ({selectedCount}개)
          </CommonModalButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default BranchSelectModal;
